/**
 * Sentry Error Monitoring
 *
 * Initialises the Sentry SDK for React. Gracefully no-ops when the DSN
 * is not configured (e.g. local development environments).
 */

import * as Sentry from '@sentry/react'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
const ENVIRONMENT = (import.meta.env.VITE_ENV as string) || 'development'

/** Whether Sentry has been successfully initialised. */
let initialised = false

/**
 * Initialise Sentry. Safe to call multiple times; subsequent calls are no-ops.
 *
 * When `VITE_SENTRY_DSN` is not set the function returns immediately so
 * that local development is not impacted.
 */
export function initSentry(): void {
  if (initialised) return

  if (!SENTRY_DSN) {
    console.debug('[Sentry] DSN not configured — error monitoring disabled')
    return
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,

    // Performance monitoring — sample 10% of transactions
    tracesSampleRate: 0.1,

    // Session replay — capture 5% of sessions during early product / UAT
    replaysSessionSampleRate: 0.05,
    // Always capture replays when an error occurs
    replaysOnErrorSampleRate: 1.0,

    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],

    // Filter out noisy errors and redact sensitive data
    beforeSend(event) {
      // Drop browser extension errors
      if (event.exception?.values?.some((e) => e.value?.includes('extension'))) {
        return null
      }
      // Redact FAC/SGC certificate numbers from the event
      const json = JSON.stringify(event)
      const redacted = json.replace(/\b(FAC|SGC)\s*[\d/-]+/gi, '[REDACTED]')
      return redacted !== json ? JSON.parse(redacted) : event
    },

    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.message) {
        breadcrumb.message = breadcrumb.message.replace(/\b(FAC|SGC)\s*[\d/-]+/gi, '[REDACTED]')
      }
      return breadcrumb
    },
  })

  initialised = true
}

/**
 * Check whether Sentry is currently initialised.
 */
export function isSentryInitialised(): boolean {
  return initialised
}

// Re-export Sentry for direct access when needed
export { Sentry }
